import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getEconomicsProductsDetail } from '../api';
import { money, StatCard, Panel, MarginCell, SuperAdminGate, EconomicsSourceNote } from './economicsShared';

function EconomicsProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    getEconomicsProductsDetail()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.role]);

  const summary = data?.summary || {};
  const products = data?.products || [];
  const currency = data?.currency || 'INR';

  return (
    <div data-testid="economics-products-page">
      <SuperAdminGate user={user}>
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/dashboard"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6B7280', textDecoration: 'none' }}
          >
            <CaretLeft size={14} /> Overview
          </Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: '16px', fontWeight: 700 }}>Revenue vs spend by product</h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Portfolio commercial view — click a product for clients, people, infrastructure, and devices.
          </p>
          <EconomicsSourceNote />
        </div>

        {loading ? <SkeletonBlock rows={6} /> : error ? (
          <EmptyState icon={ChartBar} title="Could not load" description={String(error)} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <StatCard label="Products" value={summary.product_count || 0} color="#002FA7" />
              <StatCard label="Clients" value={summary.client_count || 0} color="#0EA5E9" />
              <StatCard label="Revenue / mo" value={money(summary.revenue_monthly, currency)} color="#059669" />
              <StatCard label="Spend / mo" value={money(summary.spend_monthly, currency)} color="#DC2626" />
              <StatCard label="Margin / mo" value={money(summary.margin_monthly, currency)} color={(summary.margin_monthly || 0) >= 0 ? '#002FA7' : '#B91C1C'} />
            </div>

            <Panel title="All products">
              {products.length === 0 ? (
                <EmptyState icon={ChartBar} title="No product economics yet" description="Accounts enter client PO/billing under Accounts → Client economics. IT sets asset costs in Inventory. Payroll CTC is people cost." />
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Clients</th>
                        <th>Members</th>
                        <th>Infra</th>
                        <th>Devices</th>
                        <th>Revenue / mo</th>
                        <th>Spend / mo</th>
                        <th>People</th>
                        <th>Infra / mo</th>
                        <th>Devices / mo</th>
                        <th>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => {
                        const pid = p.product_id || '__none__';
                        return (
                          <tr
                            key={pid}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/dashboard/assets/economics/products/${encodeURIComponent(pid)}`)}
                          >
                            <td style={{ fontWeight: 600 }}>{p.product_name || pid}</td>
                            <td>{p.client_count}</td>
                            <td>{p.member_count}</td>
                            <td>{p.infra_count}</td>
                            <td>{p.device_count}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.revenue_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_people_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_infra_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_device_monthly, currency)}</td>
                            <td><MarginCell value={p.margin_monthly} pct={p.margin_pct} alwaysShow /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </>
        )}
      </SuperAdminGate>
    </div>
  );
}

export default EconomicsProductsPage;
